import type { ButtonProps, SxProps, Theme } from '@mui/material';
import { Button } from '@mui/material';
import { iconButtonClassName } from '@tix-ticketing/ui-theme';
import React, { forwardRef } from 'react';
const sizes = {
  small: {
    box: 32,
    icon: 16,
    padding: 8
  },
  medium: {
    box: 40,
    icon: 20,
    padding: 10
  },
  large: {
    box: 48,
    icon: 24,
    padding: 12
  }
} as const;
export interface IconOnlyButtonProps extends Omit<ButtonProps, 'size'> {
  size?: keyof typeof sizes;
  /** Pulls the button out by its padding so the icon lines up with that edge. */
  edge?: 'start' | 'end' | false;
  component?: React.ElementType;
}

/**
 * A square MUI Button sized for a single icon. The sizing comes before the
 * caller's `sx`, so a caller can still override it.
 */
export const IconOnlyButton = forwardRef<HTMLButtonElement, IconOnlyButtonProps>(function IconOnlyButton({
  size = 'medium',
  edge = false,
  sx,
  className,
  ...rest
}, ref) {
  const {
    box,
    icon,
    padding
  } = sizes[size];
  const sizing: SxProps<Theme> = theme => ({
    minWidth: 'unset',
    width: box,
    height: box,
    padding: `${padding}px`,
    // A theme without the radius tokens (a bare MUI theme) keeps the circle.
    ...(theme.buttonRadius === undefined && {
      borderRadius: '50%'
    }),
    ...(edge === 'start' && {
      marginLeft: `${-padding}px`
    }),
    ...(edge === 'end' && {
      marginRight: `${-padding}px`
    }),
    '& .MuiSvgIcon-root, & svg': {
      fontSize: icon,
      width: icon,
      height: icon
    }
  });
  return <Button ref={ref} size={size} className={className ? `${iconButtonClassName} ${className}` : iconButtonClassName} sx={[sizing, ...(Array.isArray(sx) ? sx : [sx])]} {...rest} />;
});